// Button (DESIGN_SYSTEM §9.2). Variants: primary, secondary (outline), ghost, danger, link.
// Sizes 36 / 44 / 48 / 56 px; 36 is only for dense desktop toolbars inside a 44 px hit row.
// Loading keeps the width and shows a spinner + the in-progress label. Blocked states keep the
// button focusable (aria-disabled) and expose the reason through aria-describedby and a tooltip.
import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import { isBlocked, type Availability } from "../state";
import { Icon, type IconName } from "./Icon";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and `loadingLabel`; the button stays in place and ignores clicks. */
  loading?: boolean;
  loadingLabel?: string;
  /** Disabled / permission-denied / plan-locked / pending-activation, each with a visible reason. */
  availability?: Availability;
  icon?: IconName;
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const base =
  "relative inline-flex items-center justify-center gap-2 rounded-sm font-semibold select-none lg:rounded-xs lg:font-medium " +
  "transition-colors duration-(--ady-dur-quick) ease-out whitespace-normal text-center";

const sizes: Record<ButtonSize, string> = {
  sm: "min-h-control px-3 text-sm",
  // D-038: desktop buttons are 36 px / 14 px; touch sizes stay on phones and tablets.
  md: "min-h-11 px-4 text-md lg:min-h-control lg:px-4.5 lg:text-sm",
  lg: "min-h-12 px-5 text-md lg:min-h-control lg:px-4.5 lg:text-sm",
  xl: "min-h-14 px-6 text-lg",
};

const variants: Record<ButtonVariant, string> = {
  // D-039: the logo gradient in its text-safe shades (white label ≥ 5.4:1 across the fill).
  primary: "ady-bg-brand text-on-primary",
  secondary: "border border-control bg-surface text-fg hover:bg-hover active:bg-selected",
  ghost: "text-link hover:bg-action-bg active:bg-selected",
  danger: "bg-danger text-on-danger hover:bg-danger-hover active:bg-danger-pressed",
  link: "min-h-11 px-0 text-link underline font-medium hover:no-underline",
};

const blockedLook = "bg-sunken text-fg-disabled border border-subtle cursor-not-allowed";
const blockedIcon: Partial<Record<Availability["kind"], IconName>> = {
  "permission-denied": "lock",
  "plan-locked": "lock",
  "pending-activation": "clock",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "lg",
    loading = false,
    loadingLabel,
    availability,
    icon,
    iconOnly = false,
    fullWidth = false,
    className,
    children,
    onClick,
    type = "button",
    "aria-describedby": describedBy,
    ...rest
  },
  ref,
) {
  const reasonId = useId();
  const blocked = isBlocked(availability);
  const inert = blocked || loading;
  // Touch has no hover: tapping a blocked button reveals its reason; Esc or blur hides it (SC 1.4.13).
  const [reasonOpen, setReasonOpen] = useState(false);
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (inert) {
      e.preventDefault();
      if (blocked) setReasonOpen(true);
      return;
    }
    onClick?.(e);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape") setReasonOpen(false);
    rest.onKeyDown?.(e);
  };
  const leadingIcon = blocked ? (blockedIcon[availability.kind] ?? icon) : icon;
  const label = loading ? (loadingLabel ?? t("common.inProgress")) : children;
  return (
    <span className={cx("group/btn relative inline-flex", fullWidth && "w-full")}>
      <button
        ref={ref}
        type={type}
        className={cx(
          base,
          sizes[size],
          blocked
            ? cx(blockedLook, variant === "link" && "border-0 bg-transparent")
            : variants[variant],
          iconOnly && "aspect-square px-0 min-w-11",
          fullWidth && "w-full",
          // The short "In progress" label must not wrap inside a button sized for its usual label.
          loading && "cursor-progress whitespace-nowrap",
          className,
        )}
        aria-disabled={inert || undefined}
        aria-busy={loading || undefined}
        aria-describedby={cx(describedBy, blocked && reasonId) || undefined}
        data-state={loading ? "loading" : blocked ? availability.kind : "enabled"}
        {...rest}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          setReasonOpen(false);
          rest.onBlur?.(e);
        }}
      >
        {loading ? (
          <span className="ady-spinner" aria-hidden="true" />
        ) : leadingIcon ? (
          <Icon name={leadingIcon} />
        ) : null}
        {iconOnly && !loading ? <span className="ady-sr-only">{label}</span> : label}
        {blocked && availability.kind === "plan-locked" ? (
          <span className="rounded-xs bg-neutral-bg px-1.5 text-2xs font-medium text-neutral-fg">
            {availability.plan}
          </span>
        ) : null}
      </button>
      {blocked ? (
        <span
          id={reasonId}
          role="tooltip"
          data-open={reasonOpen || undefined}
          className={cx(
            "ady-tooltip rounded-sm bg-inverse px-3 py-2 text-sm font-normal text-fg-inverse shadow-2",
            "invisible opacity-0 group-hover/btn:visible group-hover/btn:opacity-100",
            "group-focus-within/btn:visible group-focus-within/btn:opacity-100",
            reasonOpen && "visible opacity-100",
          )}
        >
          {availability.reason}
        </span>
      ) : null}
    </span>
  );
});
